import { COLUMN_LAYOUT_CONFIG } from "@/config/columnLayouts.data";
import { slugify, splitMultiValue, toMatchKey } from "./text";
import type { AgeGroup, ColumnDefinition, ColumnLayout, SourceRow, View } from "./types";

export { COLUMN_LAYOUT_CONFIG };

export const DEFAULT_AGE_GROUPS: AgeGroup[] = [
  { key: "Gravid", label: { no: "Gravid/spedbarn", en: "Prenatal and infancy" }, min: 0, max: 2 },
  { key: "Barnehage", label: { no: "Barnehage", en: "Early childhood" }, min: 3, max: 5 },
  { key: "Barneskole", label: { no: "Barneskole", en: "Middle childhood" }, min: 6, max: 9 },
  { key: "Mellomtrinn", label: { no: "Mellomtrinn", en: "Adolescents" }, min: 10, max: 12 },
  { key: "Ungdomsskole", label: { no: "Ungdomsskole/vgs", en: "Young adults" }, min: 13, max: 99 },
];

export function getColumnLayout(layoutId: string | null | undefined): ColumnLayout | null {
  return (layoutId && COLUMN_LAYOUT_CONFIG.layouts[layoutId]) || null;
}

/**
 * Resolves which column layout applies: an explicit override (URL parameter)
 * wins, then the view's own layout, then the configured default.
 */
export function resolveColumnLayoutId(view: View, override: string | null): string {
  if (override && getColumnLayout(override)) return override;
  if (getColumnLayout(view.columnLayoutId)) return view.columnLayoutId;
  return COLUMN_LAYOUT_CONFIG.defaultLayoutId;
}

function buildAgeColumns(ageGroups: AgeGroup[]): ColumnDefinition[] {
  return ageGroups.map((group) => ({
    key: group.key,
    label: group.label,
    tooltip: `${group.min}-${group.max}`,
  }));
}

/** Age group keys overlapping the inclusive range [youngest, oldest]. */
export function getAgeGroupKeys(
  ageGroups: AgeGroup[],
  youngest: number | null,
  oldest: number | null,
): string[] {
  if (youngest == null || oldest == null) return [];
  const low = Math.max(0, Math.min(youngest, oldest));
  const high = Math.min(120, Math.max(youngest, oldest));
  return ageGroups
    .filter((group) => high >= group.min && low <= group.max)
    .map((group) => group.key);
}

function categoryKey(value: string): string {
  return slugify(value) || `col-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Columns for a `categoryFromRow` layout: the predefined categories when
 * configured, otherwise every distinct value found in the source column.
 */
function buildCategoryColumns(layout: ColumnLayout, rows: SourceRow[]): ColumnDefinition[] {
  const predefined = layout.predefinedCategories ?? [];
  if (predefined.length) {
    const seen = new Set<string>();
    return predefined
      .map((entry) => ({
        key: entry.key.trim() || categoryKey(entry.label.no || entry.label.en),
        label: {
          ...entry.label,
          no: entry.label.no || entry.key,
          en: entry.label.en || entry.label.no || entry.key,
        },
        tooltip: "",
      }))
      .filter((def) => !seen.has(def.key) && (seen.add(def.key), true));
  }

  const defs: ColumnDefinition[] = [];
  const known = new Set<string>();
  for (const row of rows) {
    for (const value of splitMultiValue(row[layout.column ?? ""])) {
      const match = toMatchKey(value);
      if (!match || known.has(match)) continue;
      const key = categoryKey(value);
      defs.push({ key, label: { no: value, en: value }, tooltip: "" });
      known.add(match).add(toMatchKey(key));
    }
  }
  return defs;
}

function buildColumnDefinitions(
  layoutId: string,
  rows: SourceRow[],
  ageGroups: AgeGroup[],
): ColumnDefinition[] {
  const layout = getColumnLayout(layoutId);
  if (layout?.type === "categoryFromRow") return buildCategoryColumns(layout, rows);
  return buildAgeColumns(ageGroups);
}

export function buildAllColumnDefinitions(
  rows: SourceRow[],
  ageGroups: AgeGroup[],
): Record<string, ColumnDefinition[]> {
  return Object.fromEntries(
    Object.keys(COLUMN_LAYOUT_CONFIG.layouts).map((layoutId) => [
      layoutId,
      buildColumnDefinitions(layoutId, rows, ageGroups),
    ]),
  );
}

/**
 * Category column keys for one row. Values may be free text ("1 - Forebygging")
 * and are matched against keys and both language labels.
 */
export function getCategoryKeysForRow(
  row: SourceRow,
  layout: ColumnLayout,
  defs: ColumnDefinition[],
  rowCodes: Record<string, string>,
): string[] {
  const matchMap = new Map<string, string>();
  for (const def of defs) {
    matchMap.set(toMatchKey(def.key), def.key);
    matchMap.set(toMatchKey(def.label.no), def.key);
    matchMap.set(toMatchKey(def.label.en), def.key);
  }

  const sourceCode = layout.sourceViewId ? rowCodes[layout.sourceViewId] : "";
  if (sourceCode) {
    const mapped = matchMap.get(toMatchKey(sourceCode));
    if (mapped) return [mapped];
  }

  const result: string[] = [];
  for (const value of splitMultiValue(row[layout.column ?? ""])) {
    const prefix = value.match(/^(\d+)/)?.[1];
    const matched =
      matchMap.get(toMatchKey(value)) ?? (prefix ? matchMap.get(toMatchKey(prefix)) : undefined);
    if (matched && !result.includes(matched)) result.push(matched);
  }
  return result;
}

const COLOR_PALETTES: Record<string, string[] | Record<string, string>> = {
  tiltaksklasse: [
    "#759C7B",
    "#F4C872",
    "#BE513D",
    "#996633",
    "#8FC2FF",
    "#6b7280",
    "#d1495b",
    "#2e86ab",
  ],
  initialFiltering: ["#6B9471", "#A56CD6", "#8A8A8A", "#F2C94C", "#6b7280"],
  ageGroup: {
    "Gravid/spedbarn": "#759C7B",
    Barnehage: "#F4C872",
    Barneskole: "#BE513D",
    Mellomtrinn: "#996633",
    "Ungdomsskole/vgs": "#8FC2FF",
  },
};
export const REGION_PALETTE = ["#759C7B", "#F4C872", "#BE513D", "#996633", "#8FC2FF"];
export const SECTOR_PALETTE = [
  "#759C7B",
  "#F4C872",
  "#d6cda3",
  "#BE513D",
  "#996633",
  "#8FC2FF",
  "#999999",
];
const FALLBACK_PALETTE = COLOR_PALETTES.tiltaksklasse as string[];

/** Chart colour for a matrix column within the active layout's palette. */
export function getColumnColor(layoutId: string, column: ColumnDefinition, index: number): string {
  const palette = COLOR_PALETTES[getColumnLayout(layoutId)?.palette ?? "ageGroup"];
  if (palette && !Array.isArray(palette)) {
    return (
      palette[column.label.no] ?? FALLBACK_PALETTE[index % FALLBACK_PALETTE.length] ?? "#cccccc"
    );
  }
  const list = palette?.length ? palette : FALLBACK_PALETTE;
  return list[index % list.length] ?? "#cccccc";
}
